import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";

const SUGGESTIONS = [
  "How do I start as a complete beginner?",
  "What should I do if my knees feel sore when squatting?",
  "Why do we train carries and crawls?",
];

export default function CoachChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm Coach 360 — ask me anything about training, movement, or how a session works." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    const next = [...messages, { role: "user", content }];
    setMessages([...next, { role: "assistant", content: "" }]);
    setInput("");
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ messages: next }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: "Connection hiccup — try that again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="coach-chat-open"
        onClick={() => setOpen(true)}
        className={`fixed bottom-5 left-5 z-[80] bg-volt text-black font-extrabold uppercase tracking-[0.14em] text-xs px-5 py-4 shadow-[0_0_40px_-8px_rgba(255,92,26,0.6)] hover:bg-white transition-colors items-center gap-2 ${open ? "hidden" : "flex"}`}
      >
        <MessageCircle size={16} /> Coach AI
      </button>

      {open && (
        <div data-testid="coach-chat-panel"
          className="fixed bottom-5 left-5 z-[80] w-[calc(100vw-2.5rem)] max-w-sm h-[520px] max-h-[75vh] bg-zinc-950 border border-zinc-800 flex flex-col shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div>
              <p className="font-display uppercase text-xl leading-none">Coach <span className="text-volt">360</span></p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">AI training assistant · GPT</p>
            </div>
            <button data-testid="coach-chat-close" onClick={() => setOpen(false)} aria-label="Close chat"
              className="text-zinc-500 hover:text-volt transition-colors p-1">
              <X size={18} />
            </button>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-volt text-black font-semibold" : "bg-zinc-900 text-zinc-200 border border-zinc-800"}`}
                  data-testid={`chat-message-${i}`}>
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`} onClick={() => send(s)}
                    className="text-[11px] border border-zinc-700 text-zinc-400 px-3 py-2 hover:border-volt hover:text-volt transition-colors text-left">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-zinc-800 p-3 flex gap-2">
            <input
              data-testid="coach-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about training, movement, recovery…"
              className="flex-1 bg-ink border border-zinc-700 px-3 py-2.5 text-sm placeholder-zinc-600 focus:border-volt outline-none"
            />
            <button type="submit" data-testid="coach-chat-send" disabled={busy || !input.trim()}
              className="bg-volt text-black px-4 hover:bg-white transition-colors disabled:opacity-40" aria-label="Send">
              <Send size={16} />
            </button>
          </form>
          <p className="px-4 pb-2.5 text-[9px] text-zinc-600 leading-snug">
            General fitness guidance only — not medical advice. Sharp or worsening pain? See a healthcare professional.
          </p>
        </div>
      )}
    </>
  );
}
